import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts';
import {
  fetchIntegracaoRTCStatus,
  salvarIntegracaoRTC,
  testarConexaoIntegracaoRTC,
} from '../api/IntegracoesApi';
import type { SalvarIntegracaoRTCPayload } from '../types';

function useCnpjGestor() {
  const { user } = useAuth();
  return user?.cnpj ?? '';
}

export function useIntegracaoRTCStatus(cnpjContribuinte: string) {
  const { toast } = useToast();
  const cnpjGestor = useCnpjGestor();

  const query = useQuery({
    queryKey: ['integracoes', 'rtc-status', cnpjGestor, cnpjContribuinte],
    queryFn: () => fetchIntegracaoRTCStatus(cnpjContribuinte),
    staleTime: 60 * 1000,
    enabled: !!cnpjGestor && !!cnpjContribuinte,
  });

  // Falha de negócio chega como HTTP 200 com `resultado: false`, então checar só
  // `query.error` deixa passar em silêncio o caso que mais importa: se o `Status`
  // falha (tabela ausente no banco do gestor, por exemplo), `data` vem null e a tela
  // desenha "Não configurado" — indistinguível de uma empresa que de fato nunca foi
  // cadastrada. Mesmo par de checagens de `useListaUsuarios`.
  useEffect(() => {
    if (query.error) {
      toast({ variant: 'destructive', title: 'Erro ao carregar status da integração RTC' });
      return;
    }
    if (query.data && !query.data.status.resultado) {
      toast({
        variant: 'destructive',
        title: query.data.status.mensagem || 'Erro ao carregar status da integração RTC',
      });
    }
  }, [query.error, query.data, toast]);

  return query;
}

export function useSalvarIntegracaoRTC() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (payload: SalvarIntegracaoRTCPayload) => salvarIntegracaoRTC(payload),
    onSuccess: (response, variables) => {
      if (response.status.resultado) {
        toast({ title: 'Credenciais RTC salvas com sucesso.' });
        queryClient.invalidateQueries({
          queryKey: ['integracoes', 'rtc-status', cnpjGestor, variables.CNPJ_Contribuinte],
        });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao salvar credenciais RTC.' });
    },
  });
}

export function useTestarConexaoIntegracaoRTC() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cnpjGestor = useCnpjGestor();

  return useMutation({
    mutationFn: (cnpjContribuinte: string) => testarConexaoIntegracaoRTC(cnpjContribuinte),
    onSuccess: (response, cnpjContribuinte) => {
      if (response.status.resultado) {
        toast({ title: 'Conexão com a API RTC validada com sucesso.' });
      } else {
        toast({ variant: 'destructive', title: response.status.mensagem });
      }
      queryClient.invalidateQueries({
        queryKey: ['integracoes', 'rtc-status', cnpjGestor, cnpjContribuinte],
      });
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Erro ao testar conexão com a API RTC.' });
    },
  });
}
